import { useRef, useState, useCallback, useEffect, useMemo } from 'react'

/** useRequest 配置项 */
export interface UseRequestOptions<TData, TParams extends any[] = any[]> {
  /** 是否手动触发（默认 false，即自动执行） */
  manual?: boolean
  /** 首次自动执行的默认参数 */
  defaultParams?: TParams
  /** 当 ready 为 false 时不执行请求 */
  ready?: boolean
  /** 依赖项变化时重新请求 */
  refreshDeps?: any[]
  /** 轮询间隔（毫秒），设置后会自动轮询 */
  pollingInterval?: number
  /** 页面隐藏时是否继续轮询（默认 true） */
  pollingWhenHidden?: boolean
  /** 请求失败后重试次数 */
  retryCount?: number
  /** 重试间隔（毫秒） */
  retryInterval?: number
  /** 防抖等待时间（毫秒） */
  debounceWait?: number
  /** 节流等待时间（毫秒） */
  throttleWait?: number
  /** 请求前回调 */
  onBefore?: (params: TParams) => void
  /** 请求成功回调 */
  onSuccess?: (data: TData, params: TParams) => void
  /** 请求失败回调 */
  onError?: (error: Error, params: TParams) => void
  /** 请求结束回调 */
  onFinally?: (params: TParams, data?: TData, error?: Error) => void
}

/** useRequest 返回值 */
export interface UseRequestResult<TData, TParams extends any[] = any[]> {
  /** 请求返回的数据 */
  data: TData | undefined
  /** 是否正在请求 */
  loading: boolean
  /** 错误信息 */
  error: Error | undefined
  /** 当前请求的参数 */
  params: TParams | []
  /** 手动触发请求（不返回 Promise） */
  run: (...params: TParams) => void
  /** 手动触发请求（返回 Promise） */
  runAsync: (...params: TParams) => Promise<TData>
  /** 用上一次参数重新请求 */
  refresh: () => void
  /** 用上一次参数重新请求（返回 Promise） */
  refreshAsync: () => Promise<TData>
  /** 直接修改 data */
  mutate: (data: TData | ((prevData: TData | undefined) => TData)) => void
  /** 取消当前请求 */
  cancel: () => void
}

function isFunction(val: unknown): val is Function {
  return typeof val === 'function'
}

function useLatest<T>(value: T) {
  const ref = useRef(value)
  ref.current = value
  return ref
}

function useMemoizedFn<T extends (...args: any[]) => any>(fn: T): T {
  const fnRef = useLatest(fn)
  const memoizedFn = useRef<T | null>(null)
  if (!memoizedFn.current) {
    memoizedFn.current = function (this: any, ...args: any[]) {
      return fnRef.current.apply(this, args)
    } as T
  }
  return memoizedFn.current
}

export function useRequest<TData, TParams extends any[] = any[]>(
  service: (...args: TParams) => Promise<TData>,
  options: UseRequestOptions<TData, TParams> = {}
): UseRequestResult<TData, TParams> {
  const {
    manual = false,
    defaultParams,
    ready = true,
    refreshDeps,
    pollingInterval,
    pollingWhenHidden = true,
    retryCount = 0,
    retryInterval = 1000,
    debounceWait,
    throttleWait,
    onBefore,
    onSuccess,
    onError,
    onFinally,
  } = options

  const onBeforeRef = useLatest(onBefore)
  const onSuccessRef = useLatest(onSuccess)
  const onErrorRef = useLatest(onError)
  const onFinallyRef = useLatest(onFinally)
  const serviceRef = useLatest(service)

  const [data, setData] = useState<TData | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [params, setParams] = useState<TParams | []>(defaultParams || ([] as unknown as TParams))

  const countRef = useRef(0)
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastRunTimeRef = useRef(0)
  const cancelRef = useRef<(() => void) | null>(null)
  const retryCountRef = useRef(0)
  const unmountedRef = useRef(false)

  // 清理所有定时器
  const clearTimers = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current)
      throttleTimerRef.current = null
    }
  }, [])

  // 取消请求
  const cancel = useCallback(() => {
    cancelRef.current?.()
    cancelRef.current = null
    clearTimers()
  }, [clearTimers])

  // 组件卸载时清理
  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      cancel()
    }
  }, [cancel])

  // 实际执行请求的核心函数
  const _run = useCallback(
    async (_params: TParams): Promise<TData> => {
      // 取消上一次请求
      cancelRef.current?.()
      const currentCount = ++countRef.current

      onBeforeRef.current?.(_params)
      setLoading(true)
      setError(undefined)
      setParams(_params)

      try {
        const result = await (serviceRef.current as any)(..._params, (cancel: any) => {
          cancelRef.current = cancel
        })
        if (currentCount !== countRef.current || unmountedRef.current) {
          return result
        }
        setData(result)
        setError(undefined)
        onSuccessRef.current?.(result, _params)
        onFinallyRef.current?.(_params, result, undefined)
        retryCountRef.current = 0
        return result
      } catch (err) {
        if (currentCount !== countRef.current || unmountedRef.current) {
          throw err
        }
        const errorObj = err instanceof Error ? err : new Error(String(err))
        setError(errorObj)
        onErrorRef.current?.(errorObj, _params)
        onFinallyRef.current?.(_params, undefined, errorObj)

        // 重试逻辑
        if (retryCount > 0 && retryCountRef.current < retryCount) {
          retryCountRef.current++
          retryTimerRef.current = setTimeout(() => {
            _run(_params)
          }, retryInterval)
        }
        throw errorObj
      } finally {
        if (currentCount === countRef.current && !unmountedRef.current) {
          setLoading(false)
        }
      }
    },
    [retryCount, retryInterval, onBeforeRef, onSuccessRef, onErrorRef, onFinallyRef, serviceRef],
  )

  const run = useCallback(
    (...args: TParams) => {
      _run(args)
    },
    [_run],
  )

  const runAsync = useCallback(
    async (...args: TParams): Promise<TData> => {
      return _run(args)
    },
    [_run],
  )

  const refresh = useCallback(() => {
    _run(params as TParams)
  }, [_run, params])

  const refreshAsync = useCallback(async (): Promise<TData> => {
    return _run(params as TParams)
  }, [_run, params])

  const mutate = useCallback((newData: TData | ((prevData: TData | undefined) => TData)) => {
    setData((prev) => {
      if (isFunction(newData)) {
        return (newData as (prevData: TData | undefined) => TData)(prev)
      }
      return newData
    })
  }, [])

  // 带防抖/节流的 run
  const runWithControl = useMemoizedFn((...args: TParams) => {
    if (debounceWait) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        run(...args)
      }, debounceWait)
      return
    }
    if (throttleWait) {
      const now = Date.now()
      if (now - lastRunTimeRef.current < throttleWait) {
        if (throttleTimerRef.current) {
          clearTimeout(throttleTimerRef.current)
        }
        throttleTimerRef.current = setTimeout(() => {
          lastRunTimeRef.current = Date.now()
          run(...args)
        }, throttleWait - (now - lastRunTimeRef.current))
        return
      }
      lastRunTimeRef.current = now
      run(...args)
      return
    }
    run(...args)
  })

  // 带控制的 runAsync
  const runAsyncWithControl = useMemoizedFn(async (...args: TParams): Promise<TData> => {
    if (debounceWait) {
      return new Promise<TData>((resolve, reject) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }
        debounceTimerRef.current = setTimeout(() => {
          runAsync(...args).then(resolve, reject)
        }, debounceWait)
      })
    }
    if (throttleWait) {
      const now = Date.now()
      if (now - lastRunTimeRef.current < throttleWait) {
        return new Promise<TData>((resolve, reject) => {
          if (throttleTimerRef.current) {
            clearTimeout(throttleTimerRef.current)
          }
          throttleTimerRef.current = setTimeout(() => {
            lastRunTimeRef.current = Date.now()
            runAsync(...args).then(resolve, reject)
          }, throttleWait - (now - lastRunTimeRef.current))
        })
      }
      lastRunTimeRef.current = now
      return runAsync(...args)
    }
    return runAsync(...args)
  })

  // 首次自动执行
  useEffect(() => {
    if (!manual && ready) {
      const initParams = (defaultParams || []) as unknown as TParams
      run(...initParams)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // refreshDeps 变化时重新请求
  useEffect(() => {
    if (refreshDeps && !manual && ready) {
      run(...(params as TParams))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refreshDeps)

  // 轮询
  useEffect(() => {
    if (!pollingInterval || pollingInterval <= 0) {
      return
    }
    if (!ready) {
      return
    }

    const startPolling = () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current)
      }
      pollingTimerRef.current = setInterval(() => {
        refresh()
      }, pollingInterval)
    }

    const stopPolling = () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current)
        pollingTimerRef.current = null
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (!pollingWhenHidden) {
          stopPolling()
        }
      } else {
        startPolling()
      }
    }

    startPolling()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [pollingInterval, pollingWhenHidden, ready, refresh])

  return {
    data,
    loading,
    error,
    params,
    run: runWithControl,
    runAsync: runAsyncWithControl,
    refresh,
    refreshAsync,
    mutate,
    cancel,
  }
}

// 使用示例
// // 自动执行
// const { data, loading } = useRequest(getCustomizationData)

// // 纯手动触发，通过manual: true控制
// const { run, loading } = useRequest(getTokenApi, { manual: true })
// const handleLogin = async () => {
//   const res = await run({ username: 'xx', password: 'xx' })
// }

// // 自动触发 + 后续手动触发
// const { data, loading, run, runAsync } = useRequest(getTokenApi, {
//   defaultParams: [{ enterpriseCode: 'xxx', account: 'yyy' }],  // 自动执行的参数 参数需要是数组类型
// })
// // 后续手动触发，传入新参数
// const handleClick = () => {
//   run({ enterpriseCode: 'aaa', account: 'bbb' })
// }

// // 轮询 + 条件触发
// const { data } = useRequest(getMFAStatus, {
//   pollingInterval: 3000,
//   ready: mfaVisible,
// })

// // 依赖刷新
// const { data } = useRequest(getUserInfo, {
//   refreshDeps: [userId],
// })
